import React, { useState } from 'react';

export interface CandidateProfile {
  name?: string;
  title?: string;
  team?: string;
  expertise?: string;
  linkedinProfileUrl?: string | null;
}

export interface CandidateScores {
  skillMatch: number;      // 0-100, hard skills overlap
  semanticMatch: number;   // 0-100, AI semantic analysis
  teamFitBonus: number;    // 0-50, cross-position value (capped)
}

export interface CandidateChipProps {
  personId: number;
  index: number;
  totalCandidates: number;
  status: 'selected' | 'proposed' | 'rejected';
  source: 'ai_suggested' | 'user_selected' | 'drag_drop';
  matchScore?: number;
  scores?: CandidateScores; // Three-tier scoring system
  matchReasoning?: string;
  profile?: CandidateProfile;
  isSpare?: boolean; // Candidate beyond the required count
  onSelect?: () => void;
  onRemove?: () => void;
  onClick?: () => void;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDrop?: (toIndex: number) => void;
  onDragEnd?: () => void;
  isDragTarget?: boolean;
}

// Color helper functions for three-tier scoring
const getSkillMatchColor = (score: number): string => {
  if (score >= 70) return 'text-green-500';
  if (score >= 40) return 'text-yellow-400';
  return 'text-red-500';
};

const getSemanticMatchColor = (score: number): string => {
  if (score >= 80) return 'text-purple-500';
  if (score >= 50) return 'text-blue-500';
  return 'text-gray-400';
};

const getTeamFitColor = (bonus: number): string => {
  if (bonus >= 35) return 'text-green-500';
  if (bonus >= 15) return 'text-yellow-400';
  return 'text-red-500';
};

export const CandidateChip: React.FC<CandidateChipProps> = ({
  personId,
  index,
// @ts-ignore
  totalCandidates,
  status,
  source,
  matchScore,
  scores,
  matchReasoning,
  profile,
  isSpare = false,
  onSelect,
  onRemove,
  onClick,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragTarget,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isSelected = status === 'selected';
  const isProposed = status === 'proposed';
  const isAISuggested = source === 'ai_suggested';

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `candidate:${index}`);
    e.dataTransfer.setData('application/x-candidate-reorder', index.toString());
    onDragStart?.(index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('application/x-candidate-reorder')) {
      e.stopPropagation();
      onDragOver?.(index);
    }
  };

  const handleDropOnChip = (e: React.DragEvent) => {
    const data = e.dataTransfer.getData('text/plain');
    if (data && data.startsWith('candidate:')) {
      e.preventDefault();
      e.stopPropagation();
      onDrop?.(index);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    
    if (onClick) {
      onClick();
    } else if (isProposed && onSelect) {
      onSelect();
    }
  };

  // Border styles based on state
  const getBorderStyle = () => {
    if (isSpare) {
      // Spare candidate: dotted amber border
      return 'border-2 border-dashed border-amber-500/70 bg-gray-800';
    }
    if (isSelected) {
      // Selected/confirmed: solid green border
      return 'border-2 border-solid border-green-500 bg-green-900/20';
    }
    if (isProposed) {
      // Proposed: solid green border (dimmer)
      return 'border-2 border-solid border-green-500/50 bg-gray-800';
    }
    return 'border-2 border-gray-600 bg-gray-800';
  };

  // Avatar color
  const getAvatarColor = () => {
    if (isSpare) return 'bg-amber-500';
    if (isSelected) return 'bg-green-500';
    if (isProposed) return 'bg-green-600';
    return 'bg-gray-600';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDropOnChip}
      className={`
        rounded-lg px-2 py-1 transition-all cursor-pointer
        ${getBorderStyle()}
        ${isDragging ? 'opacity-50 scale-95' : ''}
        ${isDragTarget ? 'ring-2 ring-brand-blue ring-offset-1 ring-offset-dark-bg' : ''}
        hover:bg-gray-700/50
      `}
      onClick={handleClick}
      title={matchReasoning || (isAISuggested ? 'AI suggested candidate' : 'Click to view details')}
    >
      {/* Row 1: Avatar + Name + Actions */}
      <div className="flex items-center gap-1.5">
        {/* Avatar */}
        <div className={`
          w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0
          ${getAvatarColor()}
        `}>
          {profile?.name ? profile.name.charAt(0).toUpperCase() : '?'}
        </div>

        {/* Name */}
        <span className="font-semibold text-white text-sm flex-1 truncate">
          {profile?.name || `Person #${personId}`}
        </span>

        {/* Spare/reserve indicator */}
        {isSpare && (
          <span className="text-base" title="Reserve candidate">
            🪑
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Accept button */}
          {isProposed && onSelect && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              className="w-5 h-5 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-colors"
              title="Accept candidate"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
          {/* Remove button */}
          {onRemove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="w-5 h-5 flex items-center justify-center rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
              title="Remove candidate"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Three-tier scores + Job Title */}
      <div className="flex items-center gap-1.5 mt-0.5 ml-8 flex-wrap">
        {/* Three-tier scores display */}
        {scores ? (
          <>
            {/* Skills Match */}
            <span className="flex items-center gap-0.5" title={`Skills Match: ${scores.skillMatch}% direct skills overlap`}>
              <span className="text-xs">🧩</span>
              <span className={`text-xs font-bold ${getSkillMatchColor(scores.skillMatch)}`}>
                {Math.round(scores.skillMatch)}%
              </span>
            </span>
            {/* AI Semantic Match - shows reasoning on hover */}
            <span className="flex items-center gap-0.5 cursor-help" title={`AI Semantic Match: ${scores.semanticMatch}%\n${matchReasoning || 'Click for AI analysis'}`}>
              <span className="text-xs">✨</span>
              <span className={`text-xs font-bold ${getSemanticMatchColor(scores.semanticMatch)}`}>
                {Math.round(scores.semanticMatch)}%
              </span>
            </span>
            {/* Team Fit Bonus - only show if > 0 */}
            {scores.teamFitBonus > 0 && (
              <span className="flex items-center gap-0.5" title={`Team Fit Bonus: +${scores.teamFitBonus}% cross-position value`}>
                <span className="text-xs">👥</span>
                <span className={`text-xs font-bold ${getTeamFitColor(scores.teamFitBonus)}`}>
                  +{Math.round(scores.teamFitBonus)}%
                </span>
              </span>
            )}
          </>
        ) : (
          /* Fallback to legacy single score */
          <>
            {isAISuggested && (
              <span className="text-xs px-1.5 py-0.5 bg-gray-600 text-gray-300 rounded font-medium">
                AI
              </span>
            )}
            {matchScore !== undefined && (
              <span className={`text-xs font-bold ${
                matchScore >= 80 ? 'text-green-400' :
                matchScore >= 60 ? 'text-yellow-400' :
                'text-gray-400'
              }`}>
                {matchScore}%
              </span>
            )}
          </>
        )}
        {/* Job Title */}
        {profile?.title && (
          <span className="text-xs text-gray-400 truncate ml-1">
            {profile.title}
          </span>
        )}
        {profile?.linkedinProfileUrl && (
          <a
            href={
              /^https?:\/\//i.test(profile.linkedinProfileUrl)
                ? profile.linkedinProfileUrl
                : `https://${profile.linkedinProfileUrl}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-sky-400 hover:underline ml-1 shrink-0"
            title="LinkedIn profile"
            onClick={(e) => e.stopPropagation()}
          >
            in
          </a>
        )}
      </div>
    </div>
  );
};

export default CandidateChip;
